import React, { useState, useEffect } from 'react';
import './MemoryGame.css';

interface Player {
  email: string;
  name: string;
  phone?: string;
  interest?: string;
  company: string;
  id: string;
}

interface Card {
  id: number;
  iconPath: string;
  serviceName: string;
  isFlipped: boolean;
  isMatched: boolean;
}

interface MemoryGameProps {
  player: Player;
  onScoreSaved?: (score: { moves: number; player: Player }) => Promise<void>;
}

// Extract service name from icon filename
const getServiceName = (filename: string): string => {
  return filename
    .replace('Arch_', '')
    .replace('_64.svg', '')
    .replace(/-/g, ' ')
    .replace(/&/g, '&');
};

const MemoryGame: React.FC<MemoryGameProps> = ({ player, onScoreSaved }) => {
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize game
  useEffect(() => {
    const initGame = async () => {
      try {
        // Fetch icon list
        const response = await fetch('/icons/list.json');
        const iconList: string[] = await response.json();
        
        // Select 6 random icons for 12 cards (6 pairs)
        const selectedIcons = iconList
          .sort(() => Math.random() - 0.5)
          .slice(0, 6);
        
        // Create card pairs
        const gameCards: Card[] = [];
        selectedIcons.forEach((iconPath, index) => {
          const serviceName = getServiceName(iconPath);
          
          // Add two cards for each icon (pair)
          gameCards.push(
            {
              id: index * 2,
              iconPath,
              serviceName,
              isFlipped: false,
              isMatched: false,
            },
            {
              id: index * 2 + 1,
              iconPath,
              serviceName,
              isFlipped: false,
              isMatched: false,
            }
          );
        });
        
        // Shuffle cards
        const shuffledCards = gameCards.sort(() => Math.random() - 0.5);
        setCards(shuffledCards);
        setLoading(false);
      } catch (error) {
        console.error('Failed to initialize game:', error);
        setLoading(false);
      }
    };

    initGame();
  }, []);

  // Handle card click
  const handleCardClick = (cardId: number) => {
    if (gameOver || flippedCards.length >= 2) return;
    
    const card = cards.find(c => c.id === cardId);
    if (!card || card.isFlipped || card.isMatched) return;

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    // Flip the card
    setCards(prev => prev.map(c => 
      c.id === cardId ? { ...c, isFlipped: true } : c
    ));

    // Check for match when two cards are flipped
    if (newFlippedCards.length === 2) {
      setMoves(prev => prev + 1);
      
      const [firstId, secondId] = newFlippedCards;
      const firstCard = cards.find(c => c.id === firstId);
      const secondCard = cards.find(c => c.id === secondId);

      if (firstCard && secondCard && firstCard.iconPath === secondCard.iconPath) {
        // Match found
        setTimeout(() => {
          setCards(prev => prev.map(c => 
            c.id === firstId || c.id === secondId 
              ? { ...c, isMatched: true }
              : c
          ));
          setFlippedCards([]);
          
          // Check if game is complete
          const updatedCards = cards.map(c => 
            c.id === firstId || c.id === secondId 
              ? { ...c, isMatched: true }
              : c
          );
          
          if (updatedCards.every(c => c.isMatched)) {
            setGameOver(true);
            if (onScoreSaved) {
              onScoreSaved({ moves: moves + 1, player });
            }
          }
        }, 500);
      } else {
        // No match - flip cards back
        setTimeout(() => {
          setCards(prev => prev.map(c => 
            c.id === firstId || c.id === secondId 
              ? { ...c, isFlipped: false }
              : c
          ));
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  // Reset game
  const resetGame = () => {
    setCards(prev => prev.map(c => ({ 
      ...c, 
      isFlipped: false, 
      isMatched: false 
    })).sort(() => Math.random() - 0.5));
    setFlippedCards([]);
    setMoves(0);
    setGameOver(false);
  };

  if (loading) {
    return (
      <div className="memory-game">
        <div className="loading">Loading game...</div>
      </div>
    );
  }

  return (
    <div className="memory-game">
      <div className="game-header">
        <h1>AWS Memory Game</h1>
        <div className="game-stats">
          <span>Moves: {moves}</span>
          <button onClick={resetGame} className="reset-btn">
            New Game
          </button>
        </div>
      </div>

      <div className="game-grid">
        {cards.map((card) => (
          <div
            key={card.id}
            className={`card ${card.isFlipped ? 'flipped' : ''} ${card.isMatched ? 'matched' : ''}`}
            onClick={() => handleCardClick(card.id)}
          >
            <div className="card-inner">
              <div className="card-front">
                <span>?</span>
              </div>
              <div className="card-back">
                <img 
                  src={`/icons/${card.iconPath}`} 
                  alt={card.serviceName}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <span className="service-name">{card.serviceName}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {gameOver && (
        <div className="game-over">
          <h2>Congratulations!</h2>
          <p>You completed the game in {moves} moves!</p>
          <button onClick={resetGame} className="play-again-btn">
            Play Again
          </button>
        </div>
      )}
    </div>
  );
};

export default MemoryGame; 